import React from 'react'
import { useAuth } from '../../shared/contexts/AuthContext'
import { useGroupPermissions, useGlobalPermissions } from '../../hooks/usePermissions'
import { PermissionGuard } from './PermissionGuard'

interface PermissionExamplesProps {
  groupId?: string
}

export const PermissionExamples: React.FC<PermissionExamplesProps> = ({ groupId }) => {
  const { user } = useAuth()
  const globalPermissions = useGlobalPermissions(user?.id || '')
  const groupPermissions = useGroupPermissions(user?.id || '', groupId || '')

  if (!user) {
    return <div>Please log in to see permission examples</div>
  }

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow">
      <h2 className="text-2xl font-bold text-gray-900">Permission System Examples</h2>
      
      {/* Global Permissions */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Global Permissions</h3>
        
        {globalPermissions.isLoading ? (
          <div>Loading global permissions...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <h4 className="font-medium">Super Admin</h4>
              <p className="text-sm text-gray-600">
                Status: {globalPermissions.isSuperAdmin ? '✅ Granted' : '❌ Denied'}
              </p>
              <PermissionGuard
                userId={user.id}
                requiredPermission="superAdmin"
                fallback={<p className="text-red-500 text-sm">You need super admin access</p>}
              >
                <p className="text-green-500 text-sm">You can access super admin features!</p>
              </PermissionGuard>
            </div>

            <div className="p-4 border rounded-lg">
              <h4 className="font-medium">Admin</h4>
              <p className="text-sm text-gray-600">
                Status: {globalPermissions.isAdmin ? '✅ Granted' : '❌ Denied'}
              </p>
              <PermissionGuard
                userId={user.id}
                requiredPermission="admin"
                fallback={<p className="text-red-500 text-sm">You need admin access</p>}
              >
                <p className="text-green-500 text-sm">You can access admin features!</p>
              </PermissionGuard>
            </div>
          </div>
        )}
      </div>

      {/* Group Permissions */}
      {groupId && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Group Permissions</h3>
          
          {groupPermissions.isLoading ? (
            <div>Loading group permissions...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium">Group Management</h4>
                <p className="text-sm text-gray-600">
                  Status: {groupPermissions.canManage ? '✅ Granted' : '❌ Denied'}
                </p>
                <PermissionGuard
                  userId={user.id}
                  groupId={groupId}
                  requiredPermission="groupManagement"
                  fallback={<p className="text-red-500 text-sm">You need group management permissions</p>}
                >
                  <p className="text-green-500 text-sm">You can manage this group!</p>
                </PermissionGuard>
              </div>

              <div className="p-4 border rounded-lg">
                <h4 className="font-medium">Curriculum Management</h4>
                <p className="text-sm text-gray-600">
                  Status: {groupPermissions.canManageCurriculum ? '✅ Granted' : '❌ Denied'}
                </p>
                <PermissionGuard
                  userId={user.id}
                  groupId={groupId}
                  requiredPermission="curriculumManagement"
                  fallback={<p className="text-red-500 text-sm">You need curriculum management permissions</p>}
                >
                  <p className="text-green-500 text-sm">You can manage curriculum!</p>
                </PermissionGuard>
              </div>

              <div className="p-4 border rounded-lg">
                <h4 className="font-medium">Event Management</h4>
                <p className="text-sm text-gray-600">
                  Status: {groupPermissions.canManageEvents ? '✅ Granted' : '❌ Denied'}
                </p>
                <PermissionGuard
                  userId={user.id}
                  groupId={groupId}
                  requiredPermission="eventManagement"
                  fallback={<p className="text-red-500 text-sm">You need event management permissions</p>}
                >
                  <p className="text-green-500 text-sm">You can manage events!</p>
                </PermissionGuard>
              </div>

              <div className="p-4 border rounded-lg">
                <h4 className="font-medium">Content Deletion</h4>
                <p className="text-sm text-gray-600">
                  Status: {groupPermissions.canDeleteContent ? '✅ Granted' : '❌ Denied'}
                </p>
                <PermissionGuard
                  userId={user.id}
                  groupId={groupId}
                  requiredPermission="contentDeletion"
                  fallback={<p className="text-red-500 text-sm">You need content deletion permissions</p>}
                >
                  <p className="text-green-500 text-sm">You can delete content!</p>
                </PermissionGuard>
              </div>

              <div className="p-4 border rounded-lg">
                <h4 className="font-medium">Analytics Access</h4>
                <p className="text-sm text-gray-600">
                  Status: {groupPermissions.canViewAnalytics ? '✅ Granted' : '❌ Denied'}
                </p>
                <PermissionGuard
                  userId={user.id}
                  groupId={groupId}
                  requiredPermission="analyticsAccess"
                  fallback={<p className="text-red-500 text-sm">You need analytics access</p>}
                >
                  <p className="text-green-500 text-sm">You can view analytics!</p>
                </PermissionGuard>
              </div>

              <div className="p-4 border rounded-lg">
                <h4 className="font-medium">Task Assignment</h4>
                <p className="text-sm text-gray-600">
                  Status: {groupPermissions.canAssignTasks ? '✅ Granted' : '❌ Denied'}
                </p>
                <PermissionGuard
                  userId={user.id}
                  groupId={groupId}
                  requiredPermission="taskAssignment"
                  fallback={<p className="text-red-500 text-sm">You need task assignment permissions</p>}
                >
                  <p className="text-green-500 text-sm">You can assign tasks!</p>
                </PermissionGuard>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Usage Examples */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">Usage Examples</h3>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Using PermissionGuard Component</h4>
          <pre className="text-sm text-gray-700 overflow-x-auto">
{`<PermissionGuard
  userId={user.id}
  groupId={groupId}
  requiredPermission="groupManagement"
  fallback={<div>Access Denied</div>}
>
  <button>Manage Group</button>
</PermissionGuard>`}
          </pre>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Using Permission Hooks</h4>
          <pre className="text-sm text-gray-700 overflow-x-auto">
{`const permissions = useGroupPermissions(userId, groupId);

if (permissions.canManageCurriculum) {
  // Show curriculum management UI
}`}
          </pre>
        </div>
      </div>
    </div>
  )
} 